// Pure orchestration helpers for finishing a canvas round. Kept free of React,
// fetch and Firebase so the ordering/recovery rules can be unit-tested directly.

const TERMINAL_TILE_STATUSES = ['submitted', 'auto_filled'];

export function isTerminalTile(status) {
  return TERMINAL_TILE_STATUSES.includes(String(status ?? '').trim().toLowerCase());
}

/**
 * What to do when the canvas page (re)loads, from server truth (R2-P07):
 *  - 'reveal'        report already completed
 *  - 'complete_only' tile is saved/auto-filled but the report is not completed:
 *                    never redraw or re-upload, only re-run the idempotent completion
 *  - 'continue'      normal play
 */
export function reconcileOnLoad({ tileStatus, reportStatus }) {
  if (String(reportStatus ?? '').trim().toLowerCase() === 'completed') return 'reveal';
  if (isTerminalTile(tileStatus)) return 'complete_only';
  return 'continue';
}

export function isSessionEndedError(err) {
  return Boolean(err && (err.sessionEnded || err.code === 'SESSION_ENDED'));
}

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Runs `fn` and retries when the failure is worth retrying: the server says the
 * activity is not yet finalizable (ACTIVITY_NOT_EXPIRED, wait retryAfterSeconds),
 * or it is a transient network/5xx/503/429 error. Anything else (403 not a
 * participant, 409 not started, ...) fails immediately.
 *
 * Every wait is JITTERED. With ~2,000 clients failing at the same instant, an
 * identical wait makes them all retry at the same instant again (a "thundering
 * herd") and the server never recovers. `random` is injectable for tests.
 *  - transient errors: exponential backoff, "equal jitter" (half fixed, half random)
 *  - ACTIVITY_NOT_EXPIRED: the server's retryAfter plus up to `notYetJitterMs` of spread
 */
export async function withRetry(
  fn,
  {
    maxAttempts = 4,
    sleep = defaultSleep,
    maxWaitMs = 15000,
    backoffMs = 500,
    notYetJitterMs = 3000,
    random = Math.random,
  } = {}
) {
  let lastError = null;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await fn();
    } catch (err) {
      lastError = err;
      const notYet = err?.code === 'ACTIVITY_NOT_EXPIRED';
      // network failure, 5xx/503, or a throttled/timed-out request (429 from API Gateway/Lambda
      // throttling, 408) - all of which are expected during a burst and succeed a moment later
      const transient = err?.status == null || err.status >= 500 || err.status === 429 || err.status === 408;
      if (attempt === maxAttempts || !(notYet || transient)) break;
      let waitMs;
      if (notYet) {
        const base = Math.min(maxWaitMs, (Number(err.retryAfterSeconds) || 1) * 1000 + 250);
        waitMs = base + Math.floor(random() * notYetJitterMs);
      } else {
        const ceiling = Math.min(maxWaitMs, backoffMs * 2 ** (attempt - 1));
        waitMs = Math.floor(ceiling / 2 + random() * (ceiling / 2));
      }
      // eslint-disable-next-line no-await-in-loop
      await sleep(waitMs);
    }
  }
  throw lastError;
}

/**
 * Time-up for a player who drew: upload THEIR drawing first, and only then ask the server to finish the
 * round (auto-fill the tiles nobody submitted). Doing both at once let the player's own finish request
 * auto-fill their own tile while a slow upload was still in flight, replacing their drawing with the
 * coloured piece. A failed submit does not stop the finish request (the other players still need it), and
 * the error is reported, not thrown.
 */
export async function submitThenFinalize({ submit, finalize, onError = () => {} }) {
  try {
    await submit();
  } catch (err) {
    onError(err, 'submit');
  }
  try {
    return await finalize();
  } catch (err) {
    onError(err, 'finalize');
    return null;
  }
}

/**
 * How long (ms) a client may wait after the timer ends before it starts its submit
 * chain. The server only trusts a finalize `graceSeconds` after expiry, so the
 * spread must stay well inside that window: a player whose submit lands AFTER the
 * finalize would find their tile already auto-filled and lose their drawing. We use
 * 60% of the grace (default 5 s -> 3 s). Raise CANVAS_END_GRACE_SECONDS on the
 * server to spread a very large room over a longer window.
 */
export function expirySpreadMs(graceSeconds, fallbackGraceSeconds = 5) {
  const grace = Number(graceSeconds);
  const seconds = Number.isFinite(grace) && grace > 0 ? grace : fallbackGraceSeconds;
  return Math.min(15000, Math.round(seconds * 600));
}

/** A uniformly random delay in [0, spreadMs). `random` is injectable for tests. */
export function randomDelayMs(spreadMs, random = Math.random) {
  const spread = Number(spreadMs);
  return Number.isFinite(spread) && spread > 0 ? Math.floor(random() * spread) : 0;
}

/**
 * Coalesces simultaneous calls (several expiry callbacks, effect re-runs) into
 * ONE in-flight execution. A success is cached; a failure clears the slot so a
 * later explicit retry runs again.
 */
export function createSingleFlight(fn) {
  let inFlight = null;
  let succeeded = false;
  let cached;
  return function run(...args) {
    if (succeeded) return Promise.resolve(cached);
    if (inFlight) return inFlight;
    inFlight = Promise.resolve()
      .then(() => fn(...args))
      .then((value) => {
        succeeded = true;
        cached = value;
        return value;
      })
      .finally(() => {
        inFlight = null;
      });
    return inFlight;
  };
}

/**
 * R2-N01: a blank tile must be auto-filled on the server BEFORE the player's
 * stage progress is saved (the server refuses completion for a non-terminal
 * tile). Never navigates — the caller navigates only when `ok` is true, so a
 * failed finalize or failed report persistence cannot masquerade as success.
 *
 * Resolves { ok: true } or { ok: false, stage: 'finalize' | 'report', error? }.
 */
export async function finalizeThenComplete({ finalize, completeReport }) {
  try {
    await finalize();
  } catch (error) {
    return { ok: false, stage: 'finalize', error };
  }
  let saved = false;
  try {
    saved = await completeReport();
  } catch (error) {
    return { ok: false, stage: 'report', error };
  }
  return saved ? { ok: true } : { ok: false, stage: 'report' };
}
